import {MouseEvent, useEffect, useState} from 'react';
import {useTemplateHelpers} from '~/GalleryPB/hooks/useTemplateHelpers';
import {Content} from '~/GalleryPB/Template/components/QuickView/components/Content/Content';
import {Button} from '~library/atoms/Button';
import {useIsLargeAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {Footer} from './components/Footer/Footer';
import {FreePremiumToggle} from './components/FreePremiumToggle/FreePremiumToggle';
import {Header} from './components/Header/Header';
import {QuickViewAnimation} from './components/QuickViewAnimation/QuickViewAnimation';
import {Template, VariantType} from './types';

import './QuickView.sass';

type QuickViewProps = {
  open: boolean;
  hideCtas: boolean;
  onClose: () => void;
  templateId: string;
  template: Template;
  onStartCustomizingClick: (e: MouseEvent, variant: VariantType) => void;
  openQuickView: (templateId: string | undefined) => void;
};

export const QuickView = ({
  open,
  templateId,
  template,
  onClose,
  onStartCustomizingClick,
  openQuickView,
  hideCtas,
}: QuickViewProps) => {
  const isMediumAndUp = useIsMediumAndUp();
  const isLargeAndUp = useIsLargeAndUp();
  const [variant, setVariant] = useState<VariantType>(VariantType.PREMIUM);
  const [highlightFree, setHighlightFree] = useState(false);
  const isPremiumTemplate = template?.premium as boolean;
  const {checkIfQuickViewEnabled} = useTemplateHelpers();
  const isQuickViewEnabled = checkIfQuickViewEnabled(template);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', open);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [open]);

  // set event to free for specific event types
  useEffect(() => {
    if (isQuickViewEnabled) {
      if (!isPremiumTemplate) {
        setVariant(VariantType.FREE);
        setHighlightFree(true);
      }
    }
  }, [isQuickViewEnabled, isPremiumTemplate]);

  const renderModalContent = () => (
    <>
      <div className="quick-view__close-button-wrapper">
        <Button
          variant="unstyled-button"
          onClick={onClose}
          data-qa-id="pdp-go-premium"
          data-impression-tracking="true"
        >
          <CloseLineIcon ratio={0.75} />
        </Button>
      </div>
      <div className="quick-view__wrapper">
        {isLargeAndUp && <QuickViewAnimation template={template} variant={variant} key={variant} />}

        <div className="quick-view">
          {!isPremiumTemplate && <FreePremiumToggle setVariant={setVariant} variant={variant} />}

          {!isLargeAndUp && (
            <QuickViewAnimation template={template} variant={variant} key={variant} />
          )}

          <Header
            title={template?.displayName as string}
            isPremiumTemplate={isPremiumTemplate}
            templateId={templateId}
            onClose={onClose}
            openQuickView={openQuickView}
          />

          <Content variant={variant} />

          {!hideCtas && (
            <Footer
              variant={variant}
              onStartCustomizingClick={onStartCustomizingClick}
              isPremiumTemplate={isPremiumTemplate}
              highlightFree={highlightFree}
            />
          )}
        </div>
      </div>
    </>
  );

  if (!open || !template) {
    return null;
  }

  if (isMediumAndUp) {
    return (
      <ResponsiveModal
        isModal
        isOpen={open}
        onClose={onClose}
        overlayClassName="quick-view__responsive-modal"
        showHeader={false}
        closeOnClickOutside
      >
        {renderModalContent()}
      </ResponsiveModal>
    );
  }

  return (
    <BottomSheet in={open} className="quick-view__bottom-sheet" content={renderModalContent()} />
  );
};
